import { isReservedFrontUrlSegment } from '@constants/reserved-front-urls';
import { WHITELABEL_SUB_PAGES } from '@constants/whitelabel-sub-pages.generated';
import {
  getSubPageSlug,
  SUB_PAGE_URL_PATTERN
} from '@modules/whitelabel-cover-page/sub-pages/sub-pages.constants';

export const HEADER_LAYOUT_CONSTANTS = {
  LAYOUT_ID: 'header-layout',
  MENU_COUNT: 5,
  MENU_FIELD_SUFFIXES: ['label', 'url'] as const,
  SIGN_IN_LABEL: 'Sign In',
  GET_STARTED_LABEL: 'Get Started',
  SIGN_IN_LINK: '/auth/login',
  CREATE_ACCOUNT_LINK: '/auth/signup',
  CREATE_ACCOUNT_LABEL: 'Create Account',
  PREDEFINED_MENU_IDS: {
    // LINK_IN_BIO: 'link_in_bio',
    FEEDS: 'feeds',
    STORE: 'store'
  } as const,
  MENU_OPTION_IDS: {
    FEEDS: 'feeds',
    // LINK_IN_BIO: 'link_in_bio',
    STORE: 'store',
    WELCOME_VIDEO: 'welcome_video',
    EVENTS: 'events',
    NEWSLETTERS: 'newsletters'
  } as const,
  MENU_OPTION_ID_PREFIX: {
    PORTFOLIO: 'portfolio_',
    SUB_PAGE: 'sub_page_'
  } as const,
  MENU_OPTION_DISABLED_KEY: 'isLocked',
  MENU_OPTION_LABELS: {
    FEEDS: 'Feed',
    // LINK_IN_BIO: 'Link in Bio',
    STORE: 'Store'
  } as const,
  LABEL_PREDEFINED_MENUS: 'Menu Links',
  MULTISELECT_DEFAULT_LABEL: 'Select menu links',
  MULTISELECT_FILTER_LABEL: 'Search Menu Links',
  LABEL_MENU_ORDER: 'Menu Order',
  LABEL_MENU_ROW_LABEL: 'Label',
  LABEL_MENU_ROW_LINK: 'Link',
  LABEL_CUSTOM_MENUS: 'Custom Menu Links',
  LABEL_CUSTOM_MENU_ITEM: 'Custom Menu',
  LABEL_ADD_CUSTOM_MENU: 'Add Custom Menu Item',
  CUSTOM_MENU_BUTTON_DISABLED_MESSAGE:
    'Maximum menu links reached. Remove a link to add a custom menu item.',
  LABEL_EDIT_MENU: 'Edit',
  LABEL_EDIT_MENU_ARIA: 'Edit menu label',
  LABEL_REMOVE: 'Remove',
  PLACEHOLDER_MENU_LABEL: 'Enter label name',
  PLACEHOLDER_MENU_URL: 'Add link',
  CUSTOM_MENU_MODAL: {
    ADD_HEADER: 'Add Custom Link',
    EDIT_HEADER: 'Edit Custom Link',
    EDIT_LABEL_HEADER: 'Edit Feed Page Label',
    ADD_CONFIRM: 'Add Menu',
    EDIT_CONFIRM: 'Save',
    CANCEL: 'Cancel',
    LABEL_FIELD: 'Label',
    LINK_FIELD: 'Add Link',
    LABEL_REQUIRED: 'Label is required',
    LINK_REQUIRED: 'Link is required',
    LINK_INVALID: 'Link is not valid',
    LABEL_MAX_LENGTH: 'Maximum 100 characters allowed',
    LINK_MAX_LENGTH: 'Maximum 1000 characters allowed'
  },
  LOGO_ALT: 'Site logo',
  MENU_TOGGLE_ARIA: 'Open menu',
  LOGO_BUTTON_ARIA: 'Go to home',
  DATA_KEY_CTA_TEXT: 'cta_text',
  DATA_KEY_CTA_LINK: 'cta_link',
  DATA_KEY_CTA_TEXT_2: 'cta_text_2',
  DATA_KEY_CTA_LINK_2: 'cta_link_2',
  PREVIEW_MODE_LABEL: 'WHITE LABEL SITE PREVIEW MODE'
} as const;

export const HEADER_PREDEFINED_MENUS = [
  // {
  //   id: HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS.LINK_IN_BIO,
  //   label: HEADER_LAYOUT_CONSTANTS.MENU_OPTION_LABELS.LINK_IN_BIO
  // },
  {
    id: HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS.FEEDS,
    label: HEADER_LAYOUT_CONSTANTS.MENU_OPTION_LABELS.FEEDS
  },
  {
    id: HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS.STORE,
    label: HEADER_LAYOUT_CONSTANTS.MENU_OPTION_LABELS.STORE
  }
] as const;

export const HEADER_ALWAYS_SELECTED_MENU_IDS: readonly string[] = [
  HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS.FEEDS
];

export function isAlwaysSelectedHeaderMenuId(optionId: string): boolean {
  return HEADER_ALWAYS_SELECTED_MENU_IDS.includes(optionId);
}

export function isContentFeedMenuId(optionId: string): boolean {
  return optionId === HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS.FEEDS;
}

export type HeaderPredefinedMenuId =
  (typeof HEADER_PREDEFINED_MENUS)[number]['id'];

export type HeaderMenuOptionId =
  | (typeof HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS)[keyof typeof HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS]
  | `${typeof HEADER_LAYOUT_CONSTANTS.MENU_OPTION_ID_PREFIX.PORTFOLIO}${string}`;

export function getHeaderMenuFieldName(
  index: number,
  suffix: (typeof HEADER_LAYOUT_CONSTANTS.MENU_FIELD_SUFFIXES)[number]
): string {
  return `menu_${index}_${suffix}`;
}

export function isPortfolioMenuOptionId(optionId: string): boolean {
  return optionId.startsWith(HEADER_LAYOUT_CONSTANTS.MENU_OPTION_ID_PREFIX.PORTFOLIO);
}

export function isSubPageMenuOptionId(optionId: string): boolean {
  return optionId.startsWith(HEADER_LAYOUT_CONSTANTS.MENU_OPTION_ID_PREFIX.SUB_PAGE);
}

export function buildSubPageMenuOptionId(subPageUrl: string): string {
  return `${HEADER_LAYOUT_CONSTANTS.MENU_OPTION_ID_PREFIX.SUB_PAGE}${subPageUrl}`;
}

export function buildSubPagePath(username: string, subPageUrl: string): string {
  const slug = getSubPageSlug(subPageUrl, username);
  return slug ? `/${slug}` : '';
}

function isKnownSubPageSlug(username: string, slug: string): boolean {
  const pageSlug = getSubPageSlug(slug, username);
  if (!pageSlug || pageSlug === username) {
    return false;
  }
  const pages = Array.isArray(WHITELABEL_SUB_PAGES) ? WHITELABEL_SUB_PAGES : [];
  return pages.some(
    (page) => getSubPageSlug(String(page?.['url'] ?? ''), username) === pageSlug
  );
}

export function resolvePublicSubPageHref(url: string, username: string): string {
  const trimmed = (url || '').trim();
  const normalizedUsername = (username || '').trim().replace(/^\/+|\/+$/g, '');
  if (!trimmed || !normalizedUsername) {
    return trimmed;
  }
  if (/^(https?:|mailto:|tel:|#)/i.test(trimmed)) {
    return trimmed;
  }

  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 2 && segments[0] === normalizedUsername) {
    const slug = segments[1];
    if (isKnownSubPageSlug(normalizedUsername, slug)) {
      return `/${slug}`;
    }
    return trimmed;
  }
  if (segments.length !== 1) {
    return trimmed;
  }

  const slug = segments[0];
  if (slug === normalizedUsername) {
    return path;
  }
  if (isReservedFrontUrlSegment(slug, [normalizedUsername])) {
    return path;
  }
  if (!SUB_PAGE_URL_PATTERN.test(slug) || !isKnownSubPageSlug(normalizedUsername, slug)) {
    return trimmed;
  }
  return buildSubPagePath(normalizedUsername, slug);
}

export function isSubPagePathFor(
  path: string,
  username: string,
  subPageUrl: string
): boolean {
  const pathSlug = getSubPageSlug(path, username);
  const storedSlug = getSubPageSlug(subPageUrl, username);
  return !!pathSlug && pathSlug === storedSlug;
}
